import React from 'react';

interface AgentGroup {
  total: number;
  online: number;
  busy: number;
  idle: number;
  agents: any[];
}

interface AgentStatusProps {
  agents?: {
    domain?: AgentGroup;
    imap?: AgentGroup;
    warmup?: AgentGroup;
  };
}

const AgentStatus: React.FC<AgentStatusProps> = ({ agents }) => {
  const domainAgents = { total: agents?.domain?.total || 0, online: agents?.domain?.online || 0, busy: agents?.domain?.busy || 0, idle: agents?.domain?.idle || 0 };
  const imapAgents = { total: agents?.imap?.total || 0, online: agents?.imap?.online || 0, busy: agents?.imap?.busy || 0, idle: agents?.imap?.idle || 0 };
  const warmupAgents = { total: agents?.warmup?.total || 0, online: agents?.warmup?.online || 0, busy: agents?.warmup?.busy || 0, idle: agents?.warmup?.idle || 0 };

  const getUtilizationColor = (busy: number, online: number) => {
    if (online === 0) return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    const utilization = (busy / online) * 100;
    if (utilization > 80) return 'text-red-400 bg-red-500/20 border-red-500/30';
    if (utilization > 60) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    return 'text-green-400 bg-green-500/20 border-green-500/30';
  };

  return (
    <div className="h-full">
      <h2 className="text-xl font-semibold mb-4 text-white">Agent Status</h2>
      
      <div className="space-y-4">
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-white">Domain Agents</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getUtilizationColor(domainAgents.busy, domainAgents.online)}`}>
              {domainAgents.busy}/{domainAgents.online} busy
            </span>
          </div>
          <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-500"
              style={{ width: `${domainAgents.online ? (domainAgents.busy / domainAgents.online) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            {domainAgents.online ? Math.round((domainAgents.busy / domainAgents.online) * 100) : 0}% utilized · {domainAgents.online}/{domainAgents.total} online
          </p>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-white">IMAP Agents</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getUtilizationColor(imapAgents.busy, imapAgents.online)}`}>
              {imapAgents.busy}/{imapAgents.online} busy
            </span>
          </div>
          <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all duration-300 bg-gradient-to-r from-orange-500 to-yellow-500"
              style={{ width: `${imapAgents.online ? (imapAgents.busy / imapAgents.online) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            {imapAgents.online ? Math.round((imapAgents.busy / imapAgents.online) * 100) : 0}% utilized · {imapAgents.online}/{imapAgents.total} online
          </p>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-white">Warmup Agents</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getUtilizationColor(warmupAgents.busy, warmupAgents.online)}`}>
              {warmupAgents.busy}/{warmupAgents.online} busy
            </span>
          </div>
          <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all duration-300 bg-gradient-to-r from-purple-500 to-pink-500"
              style={{ width: `${warmupAgents.online ? (warmupAgents.busy / warmupAgents.online) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            {warmupAgents.online ? Math.round((warmupAgents.busy / warmupAgents.online) * 100) : 0}% utilized · {warmupAgents.online}/{warmupAgents.total} online
          </p>
        </div>
      </div>

      <div className="mt-6 p-3 bg-zinc-900/20 rounded-lg border border-zinc-800/30">
        <p className="text-sm text-zinc-400">Online: <span className="text-white font-medium">{domainAgents.online + imapAgents.online + warmupAgents.online}</span> / {domainAgents.total + imapAgents.total + warmupAgents.total} agents</p>
        <p className="text-sm text-zinc-400 mt-1">Current load: <span className="text-orange-400 font-medium">{domainAgents.busy + imapAgents.busy + warmupAgents.busy}</span> busy</p>
      </div>
    </div>
  );
};

export default AgentStatus;